import { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { errMsg } from "@/lib/api";
import { card, inputCls } from "@/components/admin/kit";
import { useGet } from "@/components/subs/common";

// Rough quality check on the phone: brightness + edge sharpness on a small copy of the photo.
export function checkPhoto(file) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const w = 200, h = Math.round((img.height / img.width) * 200) || 200;
      const c = document.createElement("canvas"); c.width = w; c.height = h;
      const x = c.getContext("2d"); x.drawImage(img, 0, 0, w, h);
      const d = x.getImageData(0, 0, w, h).data;
      const g = new Float32Array(w * h);
      let sum = 0;
      for (let i = 0; i < w * h; i++) { g[i] = 0.299 * d[i * 4] + 0.587 * d[i * 4 + 1] + 0.114 * d[i * 4 + 2]; sum += g[i]; }
      let lap = 0, n = 0;
      for (let y = 1; y < h - 1; y++) for (let k = 1; k < w - 1; k++) { const i = y * w + k; const v = 4 * g[i] - g[i - 1] - g[i + 1] - g[i - w] - g[i + w]; lap += v * v; n++; }
      const bright = sum / (w * h), sharp = lap / Math.max(n, 1);
      URL.revokeObjectURL(img.src);
      resolve(bright < 70 ? "This photo looks too dark." : sharp < 40 ? "This photo looks blurry." : "");
    };
    img.onerror = () => resolve("");
    img.src = URL.createObjectURL(file);
  });
}

export function PhotoPicker({ photos, setPhotos }) {
  const ref = useRef(null);
  const add = async (list) => {
    const next = [...photos];
    for (const f of list) { if (next.length < 3) next.push({ file: f, warn: await checkPhoto(f), url: URL.createObjectURL(f) }); }
    setPhotos(next);
  };
  return (
    <div className="grid gap-2" data-testid="enrol-photos">
      <div className="flex flex-wrap gap-2">
        {photos.map((p, i) => (
          <div key={i} className="relative">
            <img src={p.url} alt={`Photo ${i + 1}`} className="h-24 w-20 rounded-xl object-cover" />
            <button type="button" onClick={() => setPhotos(photos.filter((_, j) => j !== i))} className="absolute -right-1 -top-1 rounded-full bg-white p-0.5 shadow" aria-label="Remove photo" data-testid={`enrol-photo-remove-${i}`}><X size={14} /></button>
          </div>
        ))}
        {photos.length < 3 && (
          <button type="button" onClick={() => ref.current?.click()} className="grid h-24 w-20 place-items-center rounded-xl border-2 border-dashed border-[var(--violet)] text-xs font-bold text-[var(--violet)]" data-testid="enrol-photo-add">
            <Camera size={22} />Photo
          </button>
        )}
      </div>
      <input ref={ref} type="file" accept="image/*" capture="environment" multiple hidden onChange={(e) => { add([...e.target.files]); e.target.value = ""; }} data-testid="enrol-photo-input" />
      {photos.filter((p) => p.warn).map((p, i) => <p key={i} className="rounded-xl bg-amber-100 px-3 py-2 text-xs font-semibold text-amber-800" data-testid="enrol-photo-warning">{p.warn} Retake it if the writing is hard to read.</p>)}
    </div>
  );
}

const F0 = { photo_type: "prescription", name: "", age: "", gender: "", phone: "", receiver: "self", relationship: "", family_name: "", language: "te-IN", doctor_id: "", consent: false };

export function QuickEnrol({ api, org, onDone }) {
  const [f, setF] = useState({ ...F0, photo_type: org.type === "pharmacy" ? "pharmacy_bill" : "prescription" });
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [docs] = useGet(api, org.type === "pharmacy" ? null : "/org/doctors");
  const [rels] = useGet(api, "/org/relations");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const child = f.age !== "" && Number(f.age) < 18;
  const submit = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    photos.forEach((p) => fd.append("photos", p.file));
    Object.entries({ ...f, receiver: child ? "caregiver" : f.receiver }).forEach(([k, v]) => fd.append(k, v));
    setBusy(true);
    try { const r = (await api.post("/org/submissions", fd)).data; toast.success(`Sent to OnDose — case ${r.case_no}`); setF({ ...F0, photo_type: f.photo_type }); setPhotos([]); onDone?.(); }
    catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };
  const big = `${inputCls} h-12 min-w-0 text-base`;
  return (
    <form onSubmit={submit} className={`${card} mx-auto grid w-full min-w-0 max-w-lg grid-cols-1 gap-3`} data-testid="quick-enrol-form">
      <p className="text-2xl font-bold">Quick enrol</p>
      <div className="flex flex-wrap gap-2">{[["prescription", "Prescription"], ["pharmacy_bill", "Pharmacy bill"]].map(([k, l]) => <Button key={k} type="button" variant={f.photo_type === k ? "default" : "outline"} onClick={() => setF({ ...f, photo_type: k })} data-testid={`enrol-type-${k}`}>{l}</Button>)}</div>
      <PhotoPicker photos={photos} setPhotos={setPhotos} />
      <input className={big} placeholder="Patient name" value={f.name} onChange={set("name")} data-testid="enrol-name" />
      <div className="grid grid-cols-2 gap-2">
        <input className={big} type="number" inputMode="numeric" min={0} max={120} placeholder="Age" value={f.age} onChange={set("age")} data-testid="enrol-age" />
        <select className={big} value={f.gender} onChange={set("gender")} data-testid="enrol-gender"><option value="">Gender</option><option value="female">Female</option><option value="male">Male</option><option value="other">Other</option></select>
      </div>
      <input className={big} type="tel" inputMode="tel" placeholder="Phone number that gets the calls" value={f.phone} onChange={set("phone")} data-testid="enrol-phone" />
      {child ? <p className="rounded-xl bg-[var(--lav)] px-3 py-2 text-sm" data-testid="enrol-child-note">Under 18 — calls go to the parent.</p> : (
        <div className="flex flex-wrap gap-2">{[["self", "Patient"], ["caregiver", "Family member"]].map(([k, l]) => <Button key={k} type="button" variant={f.receiver === k ? "default" : "outline"} onClick={() => setF({ ...f, receiver: k })} data-testid={`enrol-receiver-${k}`}>{l} gets calls</Button>)}</div>
      )}
      {(child || f.receiver === "caregiver") && (
        <div className="grid grid-cols-2 gap-2">
          <input className={big} placeholder={child ? "Parent's name" : "Family member's name"} value={f.family_name} onChange={set("family_name")} data-testid="enrol-family-name" />
          {!child && <select className={big} value={f.relationship} onChange={set("relationship")} data-testid="enrol-relationship"><option value="">Patient is their…</option>{(rels || []).map(([k, w]) => <option key={k} value={k}>{k.replace("_m", " (mother's side)").replace("_p", " (father's side)")} · {w}</option>)}</select>}
        </div>
      )}
      {org.type !== "pharmacy" && <select className={big} value={f.doctor_id} onChange={set("doctor_id")} data-testid="enrol-doctor"><option value="">Doctor</option>{(docs || []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}</select>}
      <label className="flex items-start gap-3 rounded-xl bg-[var(--lav)] p-3 text-sm font-semibold"><input type="checkbox" className="mt-0.5 h-5 w-5" checked={f.consent} onChange={set("consent")} data-testid="enrol-consent" /> Patient agreed to receive reminder calls</label>
      <Button className="h-12 text-base" disabled={busy || !photos.length || !f.consent} data-testid="enrol-submit">{busy ? <><Loader2 className="animate-spin" size={16} /> Sending…</> : "Submit"}</Button>
    </form>
  );
}
